/**
 * Effective loyalty multiplier for a product, mirroring the backend
 * `loyalty-multiplier.util.ts` so the checkout / PDP preview matches what the
 * server actually awards. Combination rule: effective = max over all active
 * promotions that apply to the product's template, floored at 1.0. No stacking.
 *
 * Promotions come from `GET /loyalty/promotions/active`, which already filters
 * to enabled + in-window; `isPromotionActive` re-checks defensively so the
 * helper is safe to call with any promotion list. Dates are YYYY-MM-DD or ISO
 * strings, or null (open-ended).
 */
import type { LoyaltyPromotionDto } from "../../../api";
import { LoyaltyPromotionDtoScopeEnum } from "../../../api";

/** Active when today is within [start, end] inclusive; null bounds are open-ended. */
export function isWindowActive(
  start: string | null | undefined,
  end: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const t = now.getTime();
  if (start) {
    const s = new Date(start);
    s.setHours(0, 0, 0, 0);
    if (Number.isFinite(s.getTime()) && t < s.getTime()) return false;
  }
  if (end) {
    const e = new Date(end);
    e.setHours(23, 59, 59, 999);
    if (Number.isFinite(e.getTime()) && t > e.getTime()) return false;
  }
  return true;
}

/** null/0/negative/non-finite → 1.0 (neutral). */
export function normaliseMultiplier(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value <= 0) return 1;
  return value;
}

/** A promotion counts right now when it is enabled and today is in its window. */
export function isPromotionActive(promo: LoyaltyPromotionDto, now: Date = new Date()): boolean {
  return promo.enabled && isWindowActive(promo.startsAt, promo.endsAt, now);
}

/** ALL-scope promotions apply to everything; PRODUCTS-scope only to listed templates. */
export function appliesTo(promo: LoyaltyPromotionDto, templateId: number): boolean {
  return (
    promo.scope === LoyaltyPromotionDtoScopeEnum.All ||
    promo.productIds.includes(templateId)
  );
}

/**
 * The single highest-multiplier active promotion that applies to a product
 * template, or null when none apply. Used to surface the promo (and its end
 * date) on the product page and catalog cards.
 */
export function getActivePromotionForProduct(
  templateId: number,
  promotions: LoyaltyPromotionDto[] | undefined,
  now: Date = new Date(),
): LoyaltyPromotionDto | null {
  let best: LoyaltyPromotionDto | null = null;
  for (const promo of promotions ?? []) {
    if (!isPromotionActive(promo, now) || !appliesTo(promo, templateId)) continue;
    if (normaliseMultiplier(promo.multiplier) <= 1) continue;
    if (!best || normaliseMultiplier(promo.multiplier) > normaliseMultiplier(best.multiplier)) {
      best = promo;
    }
  }
  return best;
}

/**
 * Highest applicable, active multiplier for a product template, floored at 1.0.
 */
export function resolveEffectiveMultiplier(
  templateId: number,
  promotions: LoyaltyPromotionDto[] | undefined,
  now: Date = new Date(),
): number {
  let best = 1;
  for (const promo of promotions ?? []) {
    if (isPromotionActive(promo, now) && appliesTo(promo, templateId)) {
      best = Math.max(best, normaliseMultiplier(promo.multiplier));
    }
  }
  return best;
}
